import React, { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { Alert, Button, Empty, Input, Spin, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import ProductCategoryCard from "./components/ProductCategoryCard";
import AddCategoryModal from "./components/AddCategoryModal";
import DeleteCategoryModal from "./components/DeleteCategoryModal";
import CategoryDeletedModal from "./components/CategoryDeletedModal";
import type { AddCategorySubmitValues } from "./components/AddCategoryModal";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import martEmptyImg from "assets/images/mart-empty.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  clearCategoryError,
  createCategory,
  deleteCategory,
  fetchCategories,
  updateCategory,
  uploadCategoryImage,
} from "store/slices/categorySlice";
import type { CategoryPayload } from "types/category";

interface ProductCategoryPageProps {}

export const ProductCategoryPage: React.FunctionComponent<
  ProductCategoryPageProps
> = () => {
  const dispatch = useAppDispatch();
  const {
    actionLoading,
    categories,
    error,
    loading,
    uploadLoading,
  } = useAppSelector((state) => state.categories);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletedSuccessOpen, setDeletedSuccessOpen] = useState(false);
  const [search, setSearch] = useState("");

  const deletingCategory = useMemo(
    () => categories.find((category) => category.id === deletingId) ?? null,
    [categories, deletingId]
  );

  const editingCategory = useMemo(
    () => categories.find((category) => category.id === editingId) ?? null,
    [categories, editingId]
  );

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return categories;

    return categories.filter((category) =>
      category.name.toLowerCase().includes(keyword)
    );
  }, [categories, search]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleDeleteRequest = (id: number) => {
    dispatch(clearCategoryError());
    setDeletingId(id);
  };

  const closeDeleteModal = () => {
    if (actionLoading) return;
    setDeletingId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    const name = categories.find((category) => category.id === deletingId)?.name;
    const result = await dispatch(deleteCategory(deletingId));

    if (deleteCategory.fulfilled.match(result)) {
      setDeletingId(null);
      setDeletedSuccessOpen(true);
      message.success(`Category${name ? ` (${name})` : ""} deleted.`);
      return;
    }

    message.error(result.payload || "Unable to delete category.");
  };

  const openAddModal = () => {
    dispatch(clearCategoryError());
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = (id: number) => {
    dispatch(clearCategoryError());
    setEditingId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (actionLoading || uploadLoading) return;
    setModalOpen(false);
    setEditingId(null);
  };

  const getSelectedLogoFile = (values: AddCategorySubmitValues) =>
    values.image?.[0]?.originFileObj;

  const buildPayload = (
    values: AddCategorySubmitValues,
    logo: string
  ): CategoryPayload => ({
    name: values.name.trim(),
    logo,
  });

  const handleSubmitCategory = async (values: AddCategorySubmitValues) => {
    const selectedFile = getSelectedLogoFile(values);
    let logo = editingCategory?.logo || "";

    if (selectedFile) {
      const uploadResult = await dispatch(uploadCategoryImage(selectedFile));

      if (!uploadCategoryImage.fulfilled.match(uploadResult)) {
        message.error(
          uploadResult.payload || "Image upload failed. Please try again."
        );
        return;
      }

      logo = uploadResult.payload;
    }

    if (!logo) {
      message.error("Image upload failed. Please try again.");
      return;
    }

    const payload = buildPayload(values, logo);

    if (editingId && editingCategory) {
      const updateResult = await dispatch(
        updateCategory({ id: editingId, payload })
      );

      if (!updateCategory.fulfilled.match(updateResult)) {
        message.error(updateResult.payload || "Unable to update category.");
        return;
      }

      setModalOpen(false);
      setEditingId(null);
      message.success("Category updated.");
      return;
    }

    const createResult = await dispatch(createCategory(payload));

    if (!createCategory.fulfilled.match(createResult)) {
      message.error(createResult.payload || "Unable to create category.");
      return;
    }

    setModalOpen(false);
    message.success("Category added.");
  };

  return (
    <React.Fragment>
      <CmsTopBar title="Product Category" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="product-category-card">
            <div className="product-category-header">
              <div className="product-category-title">
                Category{" "}
                <span className="product-category-count">
                  ({String(categories.length).padStart(2, "0")})
                </span>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="product-category-create-btn"
                onClick={openAddModal}
              >
                Create New Category
              </Button>
            </div>

            <div className="mb-3" style={{ maxWidth: 420 }}>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined className="markets-search-icon" />}
                className="markets-search markets-search-full"
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                closable
                className="mb-3"
                onClose={() => dispatch(clearCategoryError())}
              />
            )}

            <div className="product-category-body">
              {loading ? (
                <div className="product-category-loading">
                  <Spin size="large" />
                </div>
              ) : filtered.length === 0 ? (
                <Empty
                  image={
                    <div
                      style={{
                        height: 130,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={martEmptyImg}
                        alt="No categories"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </div>
                  }
                  description={
                    <div style={{ textAlign: "center", marginTop: 25 }}>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: "#1f1f1f",
                        }}
                      >
                        Empty Category
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "#8c8c8c",
                          marginTop: 4,
                        }}
                      >
                        No categories found.
                      </div>
                    </div>
                  }
                />
              ) : (
                <div className="product-category-grid">
                  {filtered.map((category) => (
                    <ProductCategoryCard
                      key={category.id}
                      category={category}
                      onEdit={openEditModal}
                      onDeleteRequest={handleDeleteRequest}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Container>
      </Container>

      <AddCategoryModal
        open={modalOpen}
        mode={editingId ? "edit" : "add"}
        initialCategory={editingCategory}
        loading={actionLoading || uploadLoading}
        onClose={closeModal}
        onSubmit={handleSubmitCategory}
      />

      <DeleteCategoryModal
        open={!!deletingId}
        categoryName={deletingCategory?.name}
        loading={actionLoading}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      <CategoryDeletedModal
        open={deletedSuccessOpen}
        onClose={() => setDeletedSuccessOpen(false)}
      />
    </React.Fragment>
  );
};

export default ProductCategoryPage;
